import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Link,
  Badge,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Loading } from "../../../components/Loading";
import { financeApi } from "../apiService";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import {
  smallButtonProp,
  tableHeaderStyles,
  tableRowStyles,
  commonTypographyProps,
  commonTextFieldProps,
  commonButtonProps,
} from "../../../utils/styles";
import {
  formatCurrency,
  formatDate,
  formatInputCurrency,
  formatText,
} from "../../../utils/utils";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { PROJECT_TERMINOLOGY } from "../../../project/project_module/enums";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../components/Toast";
import { getTransactionTypeColor } from "../Transactions";

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedParty: any;
  onTransactionAdded: () => void;
  showTransactions?: boolean;
  summaryData?: any;
}

const transactionTypes = {
  Paid: "Paid",
  Received: "Received",
};

export const PAYMENT_MODES = {
  CASH: "Cash",
  ONLINE: "Online",
};

// Form validation schema
const validationSchema = Yup.object({
  transactionType: Yup.string().required("Transaction type is required"),
  paymentMode: Yup.string().required("Payment mode is required"),
  date: Yup.string().required("Date is required"),
  amount: Yup.string()
    .required("Amount is required")
    .test("is-valid-amount", "Please enter a valid amount", (value) => {
      if (!value) return false;
      const numValue = parseFloat(value.replace(/,/g, ""));
      return !isNaN(numValue) && numValue > 0;
    }),
  description: Yup.string().required("Description is required"),
});

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  selectedParty,
  onTransactionAdded,
  showTransactions = true,
  summaryData,
}) => {
  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [summary, setSummary] = useState<any>(
    summaryData || {
      totalDebit: 0,
      totalCredit: 0,
      totalBalance: 0,
      totalCount: 0,
    }
  );
  // Form initial values
  const initialValues = {
    description: "",
    amount: "",
    transactionType: transactionTypes.Paid,
    date: new Date().toISOString().split("T")[0],
    paymentMode: "CASH",
  };
  const [useBalanceAmount, setUseBalanceAmount] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open && selectedParty && showTransactions) {
      setCurrentPage(1);
      setTransactions([]);
      setHasMoreData(true);
      fetchTransactions(selectedParty.id, selectedParty.type, 1, false);
    } else if (!open) {
      setTransactions([]);
      setCurrentPage(1);
      setHasMoreData(true);
      setUseBalanceAmount(false);
    }
  }, [open, selectedParty, showTransactions]);

  // Effect to update summary when summaryData changes
  useEffect(() => {
    if (summaryData) {
      setSummary(summaryData);
    }
  }, [summaryData]);

  const fetchTransactions = async (
    partyId: string,
    partyType: string,
    page: number,
    loadMore: boolean = false
  ) => {
    if (!planStatus?.company?.id || !hasMoreData) return;

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setTransactionLoading(true);
    }

    try {
      const response = await financeApi.getTransactionsByPartyId(
        partyType,
        partyId,
        "expense",
        page,
        10 // Increased page size for better scroll experience
      );

      const newTransactions = response.transactions || [];
      const totalPages = response.pagination.totalPages || 1;
      const summary = response.summary;
      setSummary(summary);
      if (loadMore) {
        setTransactions((prev) => [...prev, ...newTransactions]);
      } else {
        setTransactions(newTransactions);
      }

      setCurrentPage(page);
      setHasMoreData(page < totalPages);
    } catch (error) {
      showToast();
    } finally {
      setTransactionLoading(false);
      setLoadingMore(false);
    }
  };

  const handleCreateTransaction = async (values: any, { resetForm }: any) => {
    if (!planStatus?.company?.id || !selectedParty) return;

    try {
      const transactionData = {
        ...values,
        partyId: selectedParty.id,
        partyType: selectedParty.type,
        paymentMode: values.paymentMode,
        context: "PAYABLE",
        description: values.description,
        contextId: selectedParty.id,
        createdBy: planStatus?.user?.id,
        amount: parseFloat(values.amount.replace(/,/g, "")),
      };

      await financeApi.createTransaction(transactionData);

      // Reset form
      resetForm({ values: initialValues });

      // Refresh transactions
      setCurrentPage(1);
      setTransactions([]);
      setHasMoreData(true);
      await fetchTransactions(selectedParty.id, selectedParty.type, 1, false);

      // Notify parent component
      onTransactionAdded();

      showToast("Transaction created successfully", "success");
    } catch (error) {
      console.error("Error creating transaction:", error);
      showToast();
    }
  };

  const handleLink = (transaction: any) => {
    switch (transaction?.context) {
      case "TRIP_EXPENSE":
        return `/trips/${transaction?.masterReferenceId}`;
      case "VEHICLE_EXPENSE":
        return `/vehicle-expense/edit/${transaction?.id}`;
      case "PROJECT_EXPENSE":
        return `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${transaction?.masterReferenceId}/${transaction?.id}`;
      case "FIXED_JOURNEY_EXPENSE":
        return `/fixed-journeys/${transaction?.masterReferenceId}`;
      case "RENTAL_JOURNEY_EXPENSE":
        return `/rental-journeys/${transaction?.masterReferenceId}`;
      case "OTHER_EXPENSE":
        return `/other-expenses/${transaction?.masterReferenceId}`;
      default:
        return "";
    }
  };

  const handleLoadMore = () => {
    if (selectedParty && hasMoreData && !loadingMore) {
      const nextPage = currentPage + 1;
      fetchTransactions(selectedParty.id, selectedParty.type, nextPage, true);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    // Load more when user scrolls to within 100px of the bottom
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      handleLoadMore();
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!selectedParty) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ minHeight: "500px" }}
      >
        <DialogTitle sx={{ px: 1, py: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div">
              {selectedParty?.name}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "grey.500" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            {/* Add Transaction Form */}
            <Card sx={{ mb: 2, bgcolor: "grey.50" }}>
              <CardContent sx={{ p: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Add New Payment
                </Typography>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleCreateTransaction}
                  enableReinitialize
                >
                  {({ values, errors, touched, setFieldValue, resetForm }) => {
                    return (
                      <Form>
                        {/* Party Summary */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="h6" sx={commonTypographyProps}>
                              Payable
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={commonTypographyProps}
                            >
                              {formatCurrency(summary?.totalDue || 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="h6" sx={commonTypographyProps}>
                              Paid
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={commonTypographyProps}
                            >
                              {formatCurrency(summary?.totalPaid || 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={2}>
                            <Typography variant="h6" sx={commonTypographyProps}>
                              Balance
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={commonTypographyProps}
                            >
                              {formatCurrency(summary?.totalBalance)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={useBalanceAmount}
                                  onChange={(e) => {
                                    setUseBalanceAmount(e.target.checked);
                                    if (e.target.checked) {
                                      if (summary?.totalBalance > 0) {
                                        setFieldValue(
                                          "transactionType",
                                          transactionTypes.Paid
                                        );
                                      } else {
                                        setFieldValue(
                                          "transactionType",
                                          transactionTypes.Received
                                        );
                                      }
                                      setFieldValue(
                                        "amount",
                                        Math.abs(
                                          summary.totalBalance
                                        ).toString()
                                      );
                                    } else {
                                      setFieldValue("amount", "");
                                    }
                                  }}
                                  size="small"
                                  sx={{
                                    "&.Mui-checked": {
                                      color: "primary.main",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  Settle All Balance
                                </Typography>
                              }
                              sx={{
                                ml: 0,
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.7rem",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={2}>
                            <FormControl
                              fullWidth
                              error={
                                touched.transactionType &&
                                Boolean(errors.transactionType)
                              }
                            >
                              <InputLabel sx={{ fontSize: "0.75rem" }}>
                                Type
                              </InputLabel>
                              <Field
                                as={Select}
                                name="transactionType"
                                label="Type"
                                {...commonTextFieldProps.SelectProps}
                              >
                                {Object.entries(transactionTypes).map(
                                  ([key, value]) => (
                                    <MenuItem
                                      key={key}
                                      value={key}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {value}
                                    </MenuItem>
                                  )
                                )}
                              </Field>
                              <ErrorMessage
                                name="transactionType"
                                component="div"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <FormControl
                              fullWidth
                              error={
                                touched.paymentMode &&
                                Boolean(errors.paymentMode)
                              }
                            >
                              <InputLabel sx={{ fontSize: "0.75rem" }}>
                                Payment Mode
                              </InputLabel>
                              <Field
                                as={Select}
                                name="paymentMode"
                                label="Payment Mode"
                                {...commonTextFieldProps.SelectProps}
                              >
                                {Object.entries(PAYMENT_MODES).map(
                                  ([key, value]) => (
                                    <MenuItem
                                      key={key}
                                      value={key}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {value}
                                    </MenuItem>
                                  )
                                )}
                              </Field>
                              <ErrorMessage
                                name="paymentMode"
                                component="div"
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <Field name="date">
                              {({ field, meta }: any) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Date"
                                  type="date"
                                  InputLabelProps={{ shrink: true }}
                                  {...commonTextFieldProps}
                                  error={meta.touched && Boolean(meta.error)}
                                  helperText={meta.touched && meta.error}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Field name="amount">
                              {({ field, meta }: any) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Amount"
                                  type="text"
                                  value={
                                    field.value
                                      ? formatInputCurrency(field.value)
                                      : ""
                                  }
                                  onChange={(e: any) => {
                                    const value = e.target.value.replace(
                                      /,/g,
                                      ""
                                    );
                                    if (
                                      value === "" ||
                                      /^\d*\.?\d*$/.test(value)
                                    ) {
                                      setFieldValue("amount", value);
                                    }
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <span
                                        style={{
                                          marginRight: 8,
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        ₹
                                      </span>
                                    ),
                                    inputProps: {
                                      style: { textAlign: "left" },
                                      maxLength: 12,
                                      inputMode: "decimal",
                                    },
                                  }}
                                  {...commonTextFieldProps}
                                  error={meta.touched && Boolean(meta.error)}
                                  helperText={meta.touched && meta.error}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Field name="description">
                              {({ field, meta }: any) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Description"
                                  {...commonTextFieldProps}
                                  multiline
                                  rows={1}
                                  placeholder="Enter description"
                                  error={meta.touched && Boolean(meta.error)}
                                  helperText={meta.touched && meta.error}
                                />
                              )}
                            </Field>
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                type="button"
                                variant="outlined"
                                onClick={() => {
                                  resetForm({ values: initialValues });
                                  onClose();
                                }}
                                color="error"
                                {...commonButtonProps}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                variant="contained"
                                {...commonButtonProps}
                              >
                                Add Payment
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Form>
                    );
                  }}
                </Formik>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            {transactionLoading && showTransactions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Loading message="Loading transactions..." />
              </Box>
            ) : showTransactions ? (
              <>
                <TableContainer
                  component={Paper}
                  sx={{ mb: 2, overflowY: "auto" }}
                  onScroll={handleScroll}
                >
                  <Table
                    size="small"
                    sx={{
                      "& .MuiTableCell-root": {
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyles}>Date</TableCell>
                        <TableCell sx={tableHeaderStyles}>
                          Transaction Details
                        </TableCell>
                        <TableCell sx={tableHeaderStyles}>Type</TableCell>
                        <TableCell sx={tableHeaderStyles}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions?.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{ textAlign: "center", py: 4 }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              No transactions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        transactions?.map((transaction, index) => (
                          <TableRow key={transaction?.id || index}>
                            <TableCell sx={tableRowStyles}>
                              {formatDate(transaction.date)}
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                                // onClick={() => navigate(handleLink(transaction))}
                              >
                                {formatText(transaction?.transactionDetails) ||
                                  formatText(transaction?.context) ||
                                  "-"}
                                {/* TODO: Add link to trip */}
                                {/* <Link
                                  href={handleLink(transaction)}
                                  sx={{ textDecoration: "none" }}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {formatText(transaction.context) || "-"}
                                  <OpenInNew sx={{ fontSize: "0.8rem" }} />
                                </Link> */}
                              </Box>
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              <Badge
                                badgeContent={transaction?.transactionType}
                                color={getTransactionTypeColor(
                                  transaction?.transactionType
                                )}
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: "0.65rem",
                                    height: 16,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={tableRowStyles}>
                              {formatCurrency(transaction?.amount || 0)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Load More Indicator */}
                {loadingMore && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <Loading message="Loading more transactions..." />
                  </Box>
                )}

                {/* End of Data Indicator */}
                {!hasMoreData && transactions?.length > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No more transactions to load
                    </Typography>
                  </Box>
                )}

                {/* Manual Load More Button (fallback) */}
                {hasMoreData && !loadingMore && transactions?.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="outlined"
                      sx={smallButtonProp.sx}
                      size="small"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      Load More
                    </Button>
                  </Box>
                )}
              </>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentModal;
